import React, {useState} from 'react';
import {Autocomplete, TextField} from '@mui/material';
import {generateLabel} from '../../../../util/util';

/***
 *
 * @param fieldName A custom name for the IyoDropDown. This should be a unique identifier.
 * @param dataList An array of data entries. Each entry must contain attributes {id, name}.
 * @param value The field to which selections get assigned. Either a number or an array of numbers, depending on
 * whether the Select is single or multiple.
 * @param handleChange Handles value change. Called with {value} holding the id of the one option that changed,
 * which is what the page's handlers expect - the consumer handler toggles the id it is given rather than
 * replacing the whole list.
 * @param isMultiple {boolean} Whether or not this component is a multi-select or single-select.
 * @returns {JSX.Element}
 * @constructor
 */
const DataStreamDropDown = ({fieldName, dataList, value, handleChange, isMultiple}) => {
  const [closed, setClosed] = useState(false);

  const inputLabel = generateLabel(fieldName, ' ');

  // The list arrives from a query, so it is null until that query resolves - and stays null
  // if it fails. Rendering an empty menu beats taking the dialog down with it.
  const options = Array.isArray(dataList) ? dataList : [];

  // The dialog holds ids; Autocomplete works in the option objects themselves. An id with no
  // option behind it - a consumer that has since been deleted, or a list still loading -
  // drops out rather than rendering a blank row.
  const optionFor = (id) => options.find((option) => option.id === id) ?? null;
  const selection = isMultiple
    ? (Array.isArray(value) ? value : []).map(optionFor).filter(Boolean)
    : optionFor(value);

  const isEmpty = isMultiple ? !selection.length : !selection;

  return (
    <Autocomplete
      id={fieldName.replace('_', '-')}
      options={options}
      value={selection}
      multiple={isMultiple}
      fullWidth
      // Nothing here is clearable in one go. Removal is per option - unticking it in the
      // list, or deleting its chip - because that is the single change the handlers take.
      disableClearable
      disableCloseOnSelect={isMultiple}
      autoHighlight
      getOptionLabel={(option) => option?.name ?? ''}
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      onChange={(event, next, reason, details) => {
        if (isMultiple) {
          if (details?.option) handleChange({value: details.option.id});
          return;
        }
        handleChange({value: next?.id ?? ''});
      }}
      onClose={() => setClosed(true)}
      renderInput={(params) => (
        <TextField
          {...params}
          label={inputLabel}
          error={isEmpty && closed}
          helperText={isEmpty && closed ? 'Required' : ''}
          variant='outlined'
        />
      )}
    />
  );
};

export default DataStreamDropDown;
